import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { InterfaithResponse } from "./types";
import { shareViaEmail, shareViaSMS, shareToFacebook } from "./utils/shareUtils";
import { saveInterfaithResponse } from "./utils/saveUtils";
import { fetchInterfaithResponse } from "./services/interfaithDialogueService";

export const useInterfaithDialogue = () => {
  const { user } = useAuth();
  const [selectedFaith, setSelectedFaith] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [response, setResponse] = useState<InterfaithResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!selectedFaith || !question.trim()) {
      toast({
        title: "Missing information",
        description: "Please select a faith group and enter your question.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchInterfaithResponse(selectedFaith, question.trim());
      
      // Ensure the response structure is valid
      if (!data || !data.response) {
        throw new Error("No response data received");
      }
      
      // Debug logging
      console.log("Raw response received in hook:", data);
      
      // Log main content sections
      console.log("Main content sections:");
      ['title', 'directAnswer', 'biblicalFoundation', 'comparisonOfBeliefs', 
       'refutationFromTheirTexts', 'theologicalReasoning', 'finalAppeal'].forEach(section => {
        if (data.response[section]) {
          console.log(`${section}: ${data.response[section].substring(0, 50)}...`);
        } else {
          console.log(`${section}: Missing`);
        }
      });
      
      // Check for scripture references
      console.log("Scripture references:", 
        Array.isArray(data.response.scriptureReferences) ? 
          data.response.scriptureReferences.length : 
          "Not an array");
      
      // Check for objections
      console.log("Objections:", 
        Array.isArray(data.response.commonObjections) ? 
          data.response.commonObjections.length : 
          "Not an array");
      
      // Ensure proper structure for scripture references
      const scriptureReferences = Array.isArray(data.response.scriptureReferences)
        ? data.response.scriptureReferences.map(ref => ({
            reference: ref.reference || "",
            text: ref.text || "",
            explanation: ref.explanation || ""
          }))
        : [];
        
      // Ensure proper structure for objections
      const commonObjections = Array.isArray(data.response.commonObjections)
        ? data.response.commonObjections.map(obj => ({
            objection: obj.objection || "",
            rebuttal: obj.rebuttal || ""
          }))
        : [];
        
      // Create the complete response with guaranteed type safety
      const safeResponse: InterfaithResponse = {
        title: data.response.title || "",
        directAnswer: data.response.directAnswer || "",
        biblicalFoundation: data.response.biblicalFoundation || "",
        scriptureReferences: scriptureReferences,
        comparisonOfBeliefs: data.response.comparisonOfBeliefs || "",
        refutationFromTheirTexts: data.response.refutationFromTheirTexts || "",
        theologicalReasoning: data.response.theologicalReasoning || "",
        commonObjections: commonObjections,
        finalAppeal: data.response.finalAppeal || ""
      };
      
      // Log final processed response
      console.log("Final processed response:", {
        hasTitle: !!safeResponse.title,
        hasDirectAnswer: !!safeResponse.directAnswer,
        hasBiblicalFoundation: !!safeResponse.biblicalFoundation,
        scriptureReferencesCount: safeResponse.scriptureReferences.length,
        hasComparisonOfBeliefs: !!safeResponse.comparisonOfBeliefs,
        hasRefutationFromTheirTexts: !!safeResponse.refutationFromTheirTexts,
        hasTheologicalReasoning: !!safeResponse.theologicalReasoning,
        commonObjectionsCount: safeResponse.commonObjections.length,
        hasFinalAppeal: !!safeResponse.finalAppeal
      });
      
      // Set the response in state
      setResponse(safeResponse);
    } catch (err: any) {
      console.error("Error fetching interfaith dialogue response:", err);
      
      // Provide a more helpful error message
      let errorMessage = "We encountered an error processing your question. Please try again.";
      
      // Add more context if available
      if (err.message) {
        if (err.message.includes("Failed to fetch")) {
          errorMessage = "Unable to connect to our interfaith dialogue service. Please check your internet connection and try again.";
        } else if (err.message.includes("404")) {
          errorMessage = "The interfaith dialogue service is temporarily unavailable. Our team has been notified.";
        }
      }
      
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add the missing handleSampleQuestion function
  const handleSampleQuestion = (sampleQuestion: string) => {
    setQuestion(sampleQuestion);
  };

  const saveResponse = async () => {
    if (!response || !user) {
      toast({
        title: "Cannot save response",
        description: user ? "No response to save" : "Please sign in to save responses",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);
      await saveInterfaithResponse(user.id, selectedFaith, question, response);
    } finally {
      setIsSaving(false);
    }
  };

  // Expose sharing methods using the utility functions
  const handleShareViaEmail = () => shareViaEmail(selectedFaith, question, response);
  const handleShareViaSMS = () => shareViaSMS(selectedFaith, question, response);
  const handleShareToFacebook = () => shareToFacebook();

  return {
    selectedFaith,
    setSelectedFaith,
    question,
    setQuestion,
    isLoading,
    isSaving,
    response,
    error,
    handleSubmit,
    handleSampleQuestion,  // Return the new function
    saveResponse,
    shareViaEmail: handleShareViaEmail,
    shareViaSMS: handleShareViaSMS,
    shareToFacebook: handleShareToFacebook
  };
};
